import { Box } from '@mui/material';

import { ensureModalityAnalysis } from '../../common/testVersions/mannWhitney';
import { MannWhitneyResultsItem } from '../../types/state';
import { getModeInterpretation } from '../../utils/helpers';

const METRIC_HEADERS = ['Metric', 'Base', 'New', 'Interpretation'];

interface MannWhitneyCompareMetricsProps {
  result: MannWhitneyResultsItem;
}

export const MannWhitneyCompareMetrics = ({
  result,
}: MannWhitneyCompareMetricsProps) => {
  if (!result) {
    return null;
  }

  const {
    mean: baseMean,
    median: baseMedian,
    variance: baseVariance,
    stddev: baseStandardDev,
    min: baseMin,
    max: baseMax,
  } = result?.base_standard_stats ?? {
    mean: null,
    median: null,
    variance: null,
    stddev: null,
    min: null,
    max: null,
  };
  const {
    mean: newMean,
    median: newMedian,
    variance: newVariance,
    stddev: newStandardDev,
    min: newMin,
    max: newMax,
  } = result?.new_standard_stats ?? {
    mean: null,
    median: null,
    variance: null,
    stddev: null,
    min: null,
    max: null,
  };
  const baseShapiroWilkPVal = result.shapiro_wilk_test_base?.pvalue ?? 'N/A';
  const newShapiroWilkPVal = result.shapiro_wilk_test_new?.pvalue ?? 'N/A';
  const baseShapiroWilkInterpretation =
    result.shapiro_wilk_test_base?.interpretation ?? 'N/A';
  const newShapiroWilkInterpretation =
    result.shapiro_wilk_test_new?.interpretation ?? 'N/A';
  // Mode counts come from the client-side modality pipeline (same one
  // backing the Mode Δ column), not from the backend's wider-bandwidth
  // Silverman KDE — otherwise this row could contradict the Mode Δ column.
  // We trigger compute on demand here (no-op if a Mode Δ sort already
  // ran). This only runs for the single expanded row, so the cost is
  // bounded — unlike eager precompute on every row at load time.
  // `isSubtest` is derived from base_parent_signature (same trick
  // RevisionRowExpandable uses) so we pick the same KDE bandwidth.
  ensureModalityAnalysis(result, result.base_parent_signature !== null);
  const baseMode = result?.baseModeCount ?? null;
  const newMode = result?.newModeCount ?? null;

  return (
    <Box
      className='mann-whitney-compare-metrics'
      sx={{
        backgroundColor: 'manWhitneyComps.compareMetricsBg',
        marginBottom: 2,
        maxWidth: '85%',
        width: '100%',
        borderRadius: '5px',
        padding: 2,
        '& .test-row-container': {
          gridTemplateColumns: '1.5fr 1fr 1fr 2fr',
          display: 'grid',
          gap: 2,
        },
        '& .test-label-row': {
          fontWeight: 'bold',
          width: '100%',
        },
      }}
    >
      <table
        style={{
          display: 'grid',
          grid: '1fr',
          gap: 2,
          flexDirection: 'column',
        }}
      >
        <thead>
          <tr className='test-row-container'>
            {METRIC_HEADERS.map((header) => (
              <th
                key={header}
                style={{ fontWeight: 'bold', textAlign: 'left' }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className='test-row-container'>
            <td>Mean</td>
            <td>{baseMean?.toFixed(2) ?? 'N/A'}</td>
            <td>{newMean?.toFixed(2) ?? 'N/A'}</td>
            <td></td>
          </tr>
          <tr className='test-row-container'>
            <td>Median</td>
            <td>{baseMedian?.toFixed(2) ?? 'N/A'}</td>
            <td>{newMedian?.toFixed(2) ?? 'N/A'}</td>
            <td></td>
          </tr>
          <tr className='test-row-container'>
            <td>Variance</td>
            <td>{baseVariance?.toFixed(2) ?? 'N/A'}</td>
            <td>{newVariance?.toFixed(2) ?? 'N/A'}</td>
            <td></td>
          </tr>
          <tr className='test-row-container'>
            <td>Standard Deviation</td>
            <td>{baseStandardDev?.toFixed(2) ?? 'N/A'}</td>
            <td>{newStandardDev?.toFixed(2) ?? 'N/A'}</td>
            <td></td>
          </tr>
          <tr className='test-row-container'>
            <td>Min</td>
            <td>{baseMin?.toFixed(2) ?? 'N/A'}</td>
            <td>{newMin?.toFixed(2) ?? 'N/A'}</td>
            <td></td>
          </tr>
          <tr className='test-row-container' style={{ marginBottom: 2 }}>
            <td>Max</td>
            <td>{baseMax?.toFixed(2) ?? 'N/A'}</td>
            <td>{newMax?.toFixed(2) ?? 'N/A'}</td>
            <td></td>
          </tr>
          <tr className='test-label-row'>
            <td>Normality Test</td>
          </tr>
          <tr className='test-row-container'>
            <td>Shapiro-Wilk</td>
            <td>{baseShapiroWilkPVal}</td>
            <td>{newShapiroWilkPVal}</td>
            <td
              style={{
                gridTemplateColumns: '1fr',
                display: 'grid',
                gap: 1,
                flexDirection: 'column',
              }}
            >
              {baseShapiroWilkInterpretation}
              <br />
              {newShapiroWilkInterpretation}
            </td>
          </tr>
          <tr className='test-label-row'>
            <td>Goodness of Fit Test</td>
          </tr>
          <tr className='test-row-container'>
            <td>Kolmogorov-Smirnov Test</td>
            <td></td>
            <td></td>
            <td>{`${result?.ks_test?.interpretation ?? ''}`}</td>
          </tr>
          <tr className='test-label-row' style={{ marginTop: 2 }}>
            <td>Distribution</td>
          </tr>
          <tr className='test-row-container'>
            <td>Estimated Modes</td>
            <td>{baseMode}</td>
            <td>{newMode}</td>
            <td>{getModeInterpretation(baseMode, newMode)}</td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
};
